import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/zip';
import { FormControl, FormGroup, FormBuilder  } from '@angular/forms';
import { ExportOptionCategory } from '../model/export-option-category.model';
import { CrawfordSnackbarService, CrawfordSpinnerService, CrawfordDownloadService,
  CrawfordPageTitleService, DialogButton, CrawfordDialogComponent } from 'crawford-shell';
import { AdvancedSearch } from '../model/advanced-search.model';
import { ExportTemplate } from '../model/export-template.model';
import { ExportFormat } from '../model/export-format.model';
import { ExportService } from './service/export.service';
import { ClaimService } from '../claim.service';
import { ClaimExportDialogComponent } from './dialog/claim-export-dialog.component';
import { UserService } from '../../user/user.service';
import { UserDetails } from '../../user/user-details.model';

@Component({
  selector: 'app-claim-export',
  templateUrl: './claim-export.component.html',
  styleUrls: ['./claim-export.component.scss']
})
export class ClaimExportComponent implements OnInit {

  @ViewChild('saveTemplateDialog') private saveTemplateDialog: ClaimExportDialogComponent;
  @ViewChild('deleteTemplateDialog') private deleteTemplateDialog: CrawfordDialogComponent;

  public exportOptionCategories: ExportOptionCategory[];
  public categoryValues = {};
  public optionValues = {};

  public exportFormats: ExportFormat[] = [];

  public disableExportButton = true;
  public showDeleteTemplateButton = false;
  public exportTemplates: ExportTemplate[] = [];
  public includeLinked = false;
  public exportLabel = 'Export';
  public templateGroup: FormGroup;
  public userDetails: UserDetails;

  private source: string;
  private savedSearch: any;

  constructor(
    private formBuilder: FormBuilder,
    private spinnerService: CrawfordSpinnerService,
    private exportService: ExportService,
    private claimService: ClaimService,
    private snackbarService: CrawfordSnackbarService,
    private downloadService: CrawfordDownloadService,
    private pageTitleService: CrawfordPageTitleService,
    private userService: UserService
  ) {
  }

  public ngOnInit(): void {
    this.userDetails = this.userService.userDetails;
    this.createFormGroup();

    this.savedSearch = this.claimService.loadSearch();
    this.includeLinked = this.savedSearch.data && this.savedSearch.data.includeLinked;

    if (this.savedSearch.data && this.savedSearch.data.claimDataSource) {
      this.source = this.savedSearch.data.claimDataSource;
    } else {
      this.source = this.claimService.claimDataSourceName;
      this.exportLabel = 'Export';
    }

    if (this.source === 'Archive') {
      this.exportLabel = 'Export From Archive';
      this.pageTitleService.setTitle('Claim Export - Archive');
    }

    Observable.zip(
      this.exportService.getExportTemplates(),
      this.exportService.getExportFormats(),
      this.exportService.getExportOptions(this.source),
    ).subscribe(
      (exportResults) => {
        this.exportTemplates = exportResults[0];
        this.setExportFormats(exportResults[1]);
        this.setExportOptions(exportResults[2]);
        this.spinnerService.stopPageSpinner();
      }
    );

    this.setSaveTemplateCallback();
  }

  private createFormGroup(): void {
    this.templateGroup = this.formBuilder.group({});
    this.templateGroup.addControl('exportTemplate', new FormControl());
    this.templateGroup.addControl('exportFormat', new FormControl());

    this.templateGroup.controls['exportTemplate'].valueChanges.subscribe(value => {
      this.parseSelectedTemplate(value);
    });
  }

  private setSaveTemplateCallback(): void {
    this.saveTemplateDialog.templateSaved.subscribe(name => {
      this.exportService.getExportTemplates().subscribe(results => {
        this.exportTemplates = results;
        const newTemplate = this.exportTemplates.find(x => x.name === name);
        this.templateGroup.controls['exportTemplate'].setValue(newTemplate.id);
      });
    });
  }

  public setExportFormats(exportFormats: ExportFormat[]): void {
    this.exportFormats = exportFormats;

    if (this.exportFormats) {
      this.templateGroup.get('exportFormat').setValue(this.exportFormats[0].id);
    }
  }

  public setExportOptions(optionCategories): void {
    optionCategories.forEach(category => {
      this.categoryValues[category.systemName] = false;
      category.options.forEach(option => {
        this.optionValues[option.systemName] = false;
      });
    });

    this.exportOptionCategories = optionCategories;
    if (this.includeLinked) {
      this.optionValues['LossReference'] = true;
      this.validateExportAvailability(null);
    }
  }

  public validateExportAvailability(optionName: string): any {
    const keys = Object.keys(this.optionValues);
    this.disableExportButton = !keys.some(key => this.optionValues[key]);
    if (this.includeLinked && optionName === 'LossReference' && !this.optionValues[optionName]) {
      this.snackbarService.displayWarningMessage('This export will include linked files.  ' +
        'Please note, you are de-selecting Loss Reference, which is the only identifier of linked files in the exported report', 20000);
    }
  }

  public toggleAll(checked: boolean): void {
    const categoryKeys = Object.keys(this.categoryValues);

    categoryKeys.forEach(key => {
      this.categoryValues[key] = checked;
    });

    const optionKeys = Object.keys(this.optionValues);

    optionKeys.forEach(key => {
      this.optionValues[key] = checked;
    });

    this.validateExportAvailability(null);
  }

  public handleAllChecked(categoryName: string): void {
    const category = this.exportOptionCategories.find(x => x.systemName === categoryName);
    const checked = this.categoryValues[categoryName];

    category.options.forEach(x => {
      this.optionValues[x.systemName] = checked;
    });
    const optionName = categoryName === 'ClaimDetails' ? 'LossReference' : null;
    this.validateExportAvailability(optionName);
  }

  public export(): void {
    this.validateExportAvailability(null);

    if (this.disableExportButton) {
      this.snackbarService.displayErrorMessage('Please select one or more columns to export');
      return;
    }

    this.spinnerService.startPageSpinner();
    this.snackbarService.displayInfoMessage('Requesting export');

    const parameters = this.mapExportValues();

    this.exportService.requestExport(parameters).subscribe(
      response => {
        this.downloadService.downloadFileFromResponse(response);
        this.spinnerService.stopPageSpinner();
      }
    );
  }

  private getSelectedColumns(): string[] {
    const optionKeys = Object.keys(this.optionValues);
    const columns: string[] = [];

    optionKeys.forEach(key => {
      if (this.optionValues[key]) {
        columns.push(key);
      }
    });

    return columns;
  }

  public mapExportValues(): any {
    if (!this.savedSearch || !(this.savedSearch instanceof AdvancedSearch)) {
      this.snackbarService.displayErrorMessage('An error occurred retrieving the search criteris for the export');
      return;
    }

    const columns = this.getSelectedColumns();
    this.savedSearch.data.exportFormat = this.templateGroup.get('exportFormat').value;

    return {
      orderBy: {
        columnName: 'CrawfordRef',
        direction: 'Ascending'
      },
      additional: this.claimService.mapFormValues(this.savedSearch.data),
      columns: columns
    };
  }

  public parseSelectedTemplate(selectedValue: number): void {
    const template = this.exportTemplates.find(x => x.id === selectedValue);

    if (!template) {
      this.showDeleteTemplateButton = false;
      return;
    }

    this.showDeleteTemplateButton = !template.isProtected;

    this.exportOptionCategories.forEach(category => {
      let allChecked = true;

      category.options.forEach(option => {
        this.optionValues[option.systemName] = false;
      });

      category.options.forEach(option => {
        if (template.columns.indexOf(option.systemName) !== -1) {
          this.optionValues[option.systemName] = true;
        } else {
          allChecked = false;
        }
      });

      this.categoryValues[category.systemName] = allChecked;
    });

    this.validateExportAvailability(null);
  }

  public showExportTemplateDialog(): void {
    const columns = this.getSelectedColumns();
    const selectedId = this.templateGroup.value['exportTemplate'];
    const selectedTemplate = this.exportTemplates.find(x => x.id === selectedId);
    this.saveTemplateDialog.openDialog(columns, this.exportTemplates, selectedTemplate);
  }

  public showDeleteTemplateDialog(): void {
    this.deleteTemplateDialog.showDialog();
  }

  public onConfirmDelete(): void {
    const selectedId = this.templateGroup.value['exportTemplate'];
    const selectedTemplate = this.exportTemplates.find(x => x.id === selectedId);

    if (!selectedTemplate) {
      return;
    }

    if (selectedTemplate.isProtected) {
      this.snackbarService.displayWarningMessage('This template is protected and cannot be deleted');
      return;
    }

    this.exportService.deleteTemplate(selectedTemplate.id).subscribe(
      () => {
        this.successfulDeleteCallback();
      }
    );
  }

  private successfulDeleteCallback() {
    this.deleteTemplateDialog.hideDialog();
    this.snackbarService.displaySuccessMessage('Template deleted successfully');
    this.exportService.getExportTemplates().subscribe(
      results => {
        this.exportTemplates = results;
        this.toggleAll(false);
      }
    );
  }

  public onCancelDelete(): void {
    this.deleteTemplateDialog.hideDialog();
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/claim/claim-export/claim-export.component.ts